import { GAME_NAME } from '@steelwake/shared';
import { colors } from '@steelwake/ui';
import { useEffect } from 'react';
import { BrowserRouter, Link, Outlet, Route, Routes } from 'react-router';

import { Account } from './routes/Account.js';
import { Credits } from './routes/Credits.js';
import { ForgotPassword } from './routes/ForgotPassword.js';
import { Hangar } from './routes/Hangar.js';
import { Home } from './routes/Home.js';
import { Leaderboard } from './routes/Leaderboard.js';
import { Login } from './routes/Login.js';
import { MatchRoute } from './routes/play/MatchRoute.js';
import { MechSelect } from './routes/play/MechSelect.js';
import { Queue } from './routes/play/Queue.js';
import { Profile } from './routes/Profile.js';
import { Register } from './routes/Register.js';
import { RequireAuth } from './routes/RequireAuth.js';
import { ResetPassword } from './routes/ResetPassword.js';
import { VerifyEmail } from './routes/VerifyEmail.js';
import { installAudio, useMenuMusic } from './audio/instance.js';
import { installSettingsSync } from './settings/sync.js';
import { useAppStore } from './store.js';

/** Header and padded main for every page except the arena, which is a full-bleed canvas. */
function Shell() {
  const user = useAppStore((s) => s.user);
  useMenuMusic();
  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Link
          to="/"
          style={{
            color: colors.accent,
            letterSpacing: '0.2em',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          {GAME_NAME}
        </Link>
        <nav style={{ display: 'flex', gap: 16 }}>
          <Link to="/play/online" data-testid="nav-play-online">
            Play online
          </Link>
          <Link to="/hangar" data-testid="nav-hangar">
            Hangar
          </Link>
          <Link to="/leaderboard" data-testid="nav-leaderboard">
            Leaderboard
          </Link>
          {user ? (
            <>
              <Link to="/profile" data-testid="nav-profile">
                Profile
              </Link>
              <Link to="/account" data-testid="nav-account">
                {user.callsign ?? user.email}
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/register" data-testid="nav-register">
                Register
              </Link>
            </>
          )}
        </nav>
      </header>
      <main style={{ flex: 1, display: 'grid', placeItems: 'start center', padding: 24 }}>
        <Outlet />
      </main>
    </div>
  );
}

export function App() {
  const refreshUser = useAppStore((s) => s.refreshUser);
  useEffect(() => {
    installAudio();
    installSettingsSync();
    void refreshUser();
  }, [refreshUser]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/play/match" element={<MatchRoute />} />
        <Route
          path="/hangar"
          element={
            <RequireAuth>
              <Hangar />
            </RequireAuth>
          }
        />
        <Route element={<Shell />}>
          <Route path="/" element={<Home />} />
          <Route path="/play" element={<MechSelect />} />
          <Route
            path="/play/online"
            element={
              <RequireAuth>
                <Queue />
              </RequireAuth>
            }
          />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/credits" element={<Credits />} />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account" element={<Account />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
